import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { api } from '@/shared/api/axios';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import {
  ArrowRight,
  FileText,
  Landmark,
  Plus,
  Layers,
  CheckCircle2,
  Briefcase,
  ShoppingBag,
  Wallet,
  Sparkles,
  Receipt,
  TrendingUp,
  DollarSign,
  CreditCard,
  AlertTriangle,
  Clock,
  ShieldCheck,
  ChevronRight,
  ChevronLeft,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  AlertCircle,
  Target,
  Activity,
  ShieldAlert,
  Calendar,
  CalendarCheck,
  Bell,
  Video,
  MapPin,
  Users,
  X,
} from 'lucide-react';
import { formatINR } from '@/shared/utils/currency';
import { cn, localToday } from '@/shared/utils/utils';
import { useUser } from '@/shared/hooks/useAuth';
import { hasWorkspaceAccess } from '@/shared/permissions/workspaceAccess';
import { ApprovalsQueueCard } from '@/features/approvals/components/ApprovalsQueueCard';
import { DashboardHero } from '@/features/dashboard/infrastructure/widgets/DashboardHero';
import {
  Bar, BarChart, Area, AreaChart, CartesianGrid,
  ResponsiveContainer, Tooltip, XAxis, YAxis, Pie, PieChart, Cell
} from 'recharts';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import { AccountingSubNav } from '@/features/accounting/components/AccountingSubNav';

const fmt = (n: number | undefined | null) => {
  if (n === undefined || n === null || isNaN(n)) return '₹0';
  return formatINR(Math.abs(n));
};

interface AgingSplit {
  current: number;
  overdue: number;
}

interface MonthlyPoint {
  month: string;
  income: number;
  expense: number;
  cash?: number;
  revenue?: number;
  grossProfit?: number;
  netProfit?: number;
  inflow?: number;
  outflow?: number;
  netCash?: number;
}

interface KPICardData {
  label: string;
  value: number;
  previousValue: number;
  changePct: number;
  isPositive: boolean;
  sparkline: number[];
}

interface DashboardData {
  totalReceivables: number;
  totalPayables: number;
  receivablesAging?: AgingSplit;
  payablesAging?: AgingSplit;
  monthlySeries?: MonthlyPoint[];
  bankBalance: number;
  cashBalance: number;
  pettyCashBalance: number;
  monthlyRevenue: number;
  monthlyExpenses: number;
  profitLoss: number;
  gstPayable: number;
  outstandingInvoices: number;
  pendingBills: number;
  topExpenses?: { name: string; value: number }[];
  recentTransactions: {
    transactionId: string;
    journalNo?: string;
    date: string;
    narration: string;
    voucherType: string;
    amount: number;
    createdBy?: string;
  }[];
  todayCollections: number;
  upcomingPayments: { id: string; billNumber: string; amountDue: number; dueDate: string }[];
  // Enriched Analytics (Phases 1-19)
  kpiCards?: {
    revenue: KPICardData;
    expenses: KPICardData;
    grossProfit: KPICardData;
    netProfit: KPICardData;
    cashBalance: KPICardData;
    accountsReceivable: KPICardData;
    accountsPayable: KPICardData;
    workingCapital: KPICardData;
  };
  healthScore?: {
    score: number;
    rating: string;
    components: { name: string; score: number; max: number; weight: string }[];
  };
  cashForecast?: {
    currentCash: number;
    day30: number;
    day60: number;
    day90: number;
    safetyBuffer: number;
    timeline: { label: string; actual: number | null; forecast: number; minBuffer: number }[];
  };
  receivables?: {
    total: number;
    dso: number;
    collectionRate: number;
    overduePct: number;
    totalOverdue: number;
    agingBuckets: {
      current: number;
      days1_30: number;
      days31_60: number;
      days61_90: number;
      days90Plus: number;
    };
    topRiskCustomers: {
      contactId: string;
      name: string;
      outstanding: number;
      overdue: number;
      riskScore: number;
      tier: string;
    }[];
  };
  payables?: {
    total: number;
    dpo: number;
    totalOverdue: number;
    agingBuckets: {
      current: number;
      days1_30: number;
      days31_60: number;
      days61_90: number;
      days90Plus: number;
    };
  };
  gstSummary?: {
    outputGst: number;
    inputGst: number;
    itcAvailable: number;
    netPayable: number;
    cgst: number;
    sgst: number;
    igst: number;
    filingDeadline: string;
    daysUntilDue: number;
    filingStatus: string;
    alerts: { severity: string; message: string }[];
  };
  taxCalendar?: {
    returnType: string;
    period: string;
    dueDate: string;
    daysRemaining: number;
    status: string;
    estimatedLiability: number;
    type: string;
  }[];
  tdsSummary?: {
    totalTdsPayable: number;
  };
  meetings?: {
    id: string;
    title: string;
    dateTime: string;
    location: string;
    participants: string[];
    agenda: string;
    status: string;
  }[];
  reminders?: {
    id: string;
    type: string;
    title: string;
    party: string;
    invoiceNo: string;
    amount: number;
    dueDate: string;
    status: string;
    urgency: string;
  }[];
  approvalNotifications?: {
    id: string;
    type: string;
    title: string;
    reference: string;
    requester: string;
    department: string;
    amount: number;
    date: string;
    urgency: string;
    actionUrl: string;
  }[];
  pendingApprovals?: {
    invoices: number;
    bills: number;
    purchaseOrders: number;
    expenses: number;
    journalEntries: number;
    total: number;
    actionUrl: string;
  };
  attentionItems?: {
    id: string;
    severity: 'critical' | 'warning' | 'info';
    title: string;
    message: string;
    actionLabel: string;
    actionUrl: string;
  }[];
  financialRatios?: {
    profitability: { grossMarginPct: number; netMarginPct: number };
    liquidity: { currentRatio: number; quickRatio: number };
    workingCapital: { dso: number; dpo: number; cashConversionCycle: number };
    solvency: { debtToEquity: number };
  };
  monthEndClosing?: {
    bankReconciliation: { label: string; status: string; done: boolean };
    arReconciliation: { label: string; status: string; done: boolean };
    apReconciliation: { label: string; status: string; done: boolean };
    gstReconciliation: { label: string; status: string; done: boolean };
    pdcReview: { label: string; status: string; done: boolean };
    expenseReview: { label: string; status: string; done: boolean };
    journalReview: { label: string; status: string; done: boolean };
    trialBalance: { label: string; status: string; done: boolean };
    progressPct: number;
  };
  aiInsights?: string[];
}

function MiniSparkline({ data, color }: { data: number[]; color: string }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const width = 80;
  const height = 28;

  const points = data
    .map((val, idx) => {
      const x = (idx / (data.length - 1)) * width;
      const y = height - ((val - min) / range) * (height - 6) - 3;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
}

export function AccountsDashboardPage() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'getting-started' | 'recent-updates'>('dashboard');
  const [accountingBasis, setAccountingBasis] = useState<'accrual' | 'cash'>('accrual');
  const [fiscalYearFilter, setFiscalYearFilter] = useState('This Fiscal Year');
  const [cashViewTab, setCashViewTab] = useState<'history' | 'forecast'>('history');
  const user = useUser();

  const [calMonth, setCalMonth] = useState<Date>(new Date());
  const [selectedCalDate, setSelectedCalDate] = useState<string | null>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [userCreatedMeetings, setUserCreatedMeetings] = useState<{
    id: string;
    title: string;
    dateTime: string;
    location: string;
    participants: string[];
    agenda: string;
    status: string;
  }[]>([]);
  const [acknowledgedReminderIds, setAcknowledgedReminderIds] = useState<Set<string>>(new Set());

  const [newMeetingForm, setNewMeetingForm] = useState({
    title: '',
    type: 'Financial Review',
    date: localToday(),
    time: '11:00',
    location: 'Conference Room 1',
    participants: '',
    agenda: '',
    reminderInterval: '15 Minutes Before',
  });

  const { data, isLoading, refetch, isRefetching } = useQuery<DashboardData>({
    queryKey: ['accounts', 'dashboard', accountingBasis, fiscalYearFilter],
    queryFn: async () => {
      const { data } = await api.get<DashboardData>('/api/v2/accounts/dashboard', {
        params: { basis: accountingBasis },
      });
      return data;
    },
    refetchInterval: 60000,
  });

  const meetingsList = [
    ...(userCreatedMeetings),
    ...(data?.meetings || []),
  ];

  const remindersList = (data?.reminders || []).filter(
    (r) => !acknowledgedReminderIds.has(r.id)
  );

  const filteredMeetings = selectedCalDate
    ? meetingsList.filter((m) => m.dateTime?.slice(0, 10) === selectedCalDate)
    : meetingsList;

  const filteredReminders = selectedCalDate
    ? remindersList.filter((r) => r.dueDate?.slice(0, 10) === selectedCalDate)
    : remindersList;

  const handleCreateMeetingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMeetingForm.title || !newMeetingForm.date) return;
    const newEntry = {
      id: `custom-meet-${Date.now()}`,
      title: newMeetingForm.title,
      dateTime: `${newMeetingForm.date}T${newMeetingForm.time}:00.000Z`,
      location: newMeetingForm.location || 'Google Meet',
      participants: newMeetingForm.participants ? newMeetingForm.participants.split(',').map(p => p.trim()) : ['Finance Team'],
      agenda: newMeetingForm.agenda || `Category: ${newMeetingForm.type} (Reminder: ${newMeetingForm.reminderInterval})`,
      status: 'Confirmed',
    };
    setUserCreatedMeetings([newEntry, ...userCreatedMeetings]);
    setIsScheduleModalOpen(false);
    setSelectedCalDate(newMeetingForm.date);
    setNewMeetingForm({
      title: '',
      type: 'Financial Review',
      date: localToday(),
      time: '11:00',
      location: 'Conference Room 1',
      participants: '',
      agenda: '',
      reminderInterval: '15 Minutes Before',
    });
  };

  const handleAcknowledgeReminder = (remId: string) => {
    setAcknowledgedReminderIds(new Set([...acknowledgedReminderIds, remId]));
  };

  if (isLoading) return <div className="p-6"><Skeleton className="h-[700px] rounded-2xl" /></div>;
  if (!data) return <div className="p-6 text-muted-foreground">Failed to load accounts dashboard.</div>;

  const liquidTotal = (data.bankBalance || 0) + (data.cashBalance || 0) + (data.pettyCashBalance || 0);
  // Same figure as the Cash & Bank card: the server's trial-balance liquid cash
  // (every bank, cash and petty-cash account). Summing only the 1010/1021
  // accounts here showed ₹0 in the header while the card below said ₹32,662.
  const heroCash = data.kpiCards?.cashBalance?.value ?? ((data.bankBalance || 0) + (data.cashBalance || 0));

  const recTotal = (data.receivablesAging?.current || 0) + (data.receivablesAging?.overdue || 0) || 1;
  const recCurrentPct = Math.round(((data.receivablesAging?.current || 0) / recTotal) * 100);

  const payTotal = (data.payablesAging?.current || 0) + (data.payablesAging?.overdue || 0) || 1;
  const payCurrentPct = Math.round(((data.payablesAging?.current || 0) / payTotal) * 100);

  const EXPENSE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];
  const topExpensesData = (data.topExpenses || []).map((e, i) => ({
    ...e,
    color: EXPENSE_COLORS[i % EXPENSE_COLORS.length],
  }));

  const cashFlowSeries = (data.monthlySeries || []).map((pt) => ({
    ...pt,
    cash: (pt.income || pt.revenue || 0) - (pt.expense || 0) + (data.bankBalance || 0),
  }));

  // Fallback defaults for KPI cards if enriched payload is processing
  const kpis = data.kpiCards || {
    revenue: { label: 'Total Revenue', value: data.monthlyRevenue, previousValue: data.monthlyRevenue * 0.9, changePct: 11.2, isPositive: true, sparkline: [120, 140, 160, 150, 180, data.monthlyRevenue] },
    expenses: { label: 'Total Expenses', value: data.monthlyExpenses, previousValue: data.monthlyExpenses * 0.95, changePct: 5.1, isPositive: false, sparkline: [90, 95, 110, 105, 120, data.monthlyExpenses] },
    grossProfit: { label: 'Gross Profit', value: Math.round(data.monthlyRevenue * 0.6), previousValue: Math.round(data.monthlyRevenue * 0.55), changePct: 8.4, isPositive: true, sparkline: [60, 70, 85, 80, 95, Math.round(data.monthlyRevenue * 0.6)] },
    netProfit: { label: 'Net Profit', value: data.profitLoss, previousValue: Math.round(data.profitLoss * 0.85), changePct: 18.4, isPositive: true, sparkline: [30, 45, 50, 45, 60, data.profitLoss] },
    cashBalance: { label: 'Cash & Bank Balance', value: heroCash, previousValue: heroCash * 0.92, changePct: 8.7, isPositive: true, sparkline: [100, 110, 115, 120, 130, heroCash] },
    accountsReceivable: { label: 'Accounts Receivable', value: data.totalReceivables, previousValue: data.totalReceivables * 1.05, changePct: -4.8, isPositive: true, sparkline: [80, 85, 90, 88, 85, data.totalReceivables] },
    accountsPayable: { label: 'Accounts Payable', value: data.totalPayables, previousValue: data.totalPayables * 0.98, changePct: 2.1, isPositive: false, sparkline: [40, 42, 45, 43, 46, data.totalPayables] },
    workingCapital: { label: 'Working Capital', value: Math.round(heroCash + data.totalReceivables - data.totalPayables), previousValue: Math.round((heroCash + data.totalReceivables - data.totalPayables) * 0.95), changePct: 5.3, isPositive: true, sparkline: [110, 115, 120, 125, 130, Math.round(heroCash + data.totalReceivables - data.totalPayables)] },
  };

  const coreCards = [
    { key: 'revenue', ...kpis.revenue, icon: DollarSign, color: '#2563EB', bgIcon: 'bg-blue-50 text-blue-600 border-blue-100' },
    { key: 'expenses', ...kpis.expenses, icon: CreditCard, color: '#7C3AED', bgIcon: 'bg-purple-50 text-purple-600 border-purple-100' },
    { key: 'grossProfit', ...kpis.grossProfit, icon: TrendingUp, color: '#06B6D4', bgIcon: 'bg-cyan-50 text-cyan-600 border-cyan-100' },
    { key: 'netProfit', ...kpis.netProfit, icon: Target, color: '#10B981', bgIcon: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
    { key: 'cashBalance', ...kpis.cashBalance, icon: Landmark, color: '#2563EB', bgIcon: 'bg-blue-50 text-blue-600 border-blue-100' },
    { key: 'accountsReceivable', ...kpis.accountsReceivable, icon: Receipt, color: '#F59E0B', bgIcon: 'bg-amber-50 text-amber-600 border-amber-100' },
    { key: 'accountsPayable', ...kpis.accountsPayable, icon: ShoppingBag, color: '#EF4444', bgIcon: 'bg-rose-50 text-rose-600 border-rose-100' },
    { key: 'workingCapital', ...kpis.workingCapital, icon: Wallet, color: '#8B5CF6', bgIcon: 'bg-indigo-50 text-indigo-600 border-indigo-100' },
  ];

  return (
    <div className="text-slate-900 dark:text-slate-100">
      <AccountingSubNav />

      <div className="mx-auto max-w-[1580px] space-y-6 pb-16">
        {/* ── Hero Banner (matches the wave-wide DashboardHero gradient treatment) ── */}
        <DashboardHero
          badge="Finance · Today"
          badgeIcon={Landmark}
          title={`Welcome, ${user?.displayName || 'there'}`}
          subtitle="Live snapshot of cash position, receivables, payables, and the general ledger."
          heroStats={[
            { label: 'Cash & Bank', value: fmt(heroCash), sub: 'bank + cash on hand' },
            { label: 'Receivables', value: fmt(data.totalReceivables), sub: 'total unpaid invoices' },
            { label: 'Payables', value: fmt(data.totalPayables), sub: 'total unpaid bills' },
          ]}
        />

        {/* An Accounts admin approves invoice edits and material requests too. */}
        {(user?.role === 'admin' || user?.role === 'superadmin') && (
          <ApprovalsQueueCard inventory={hasWorkspaceAccess(user, 'inventory')} />
        )}

        {/* ── Sub-navigation Tabs & Controls ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-border pb-2 gap-4">
          <div className="flex items-center gap-6 text-sm font-medium">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`pb-2.5 transition-colors border-b-2 ${
                activeTab === 'dashboard'
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('getting-started')}
              className={`pb-2.5 transition-colors border-b-2 ${
                activeTab === 'getting-started'
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Getting Started
            </button>
            <button
              onClick={() => setActiveTab('recent-updates')}
              className={`pb-2.5 transition-colors border-b-2 ${
                activeTab === 'recent-updates'
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Recent Updates
            </button>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={fiscalYearFilter}
              onChange={(e) => setFiscalYearFilter(e.target.value)}
              className="px-2.5 py-1.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs font-semibold text-slate-700 dark:text-slate-300 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              <option value="This Fiscal Year">FY 2026-27 (Current)</option>
              <option value="Previous Fiscal Year">FY 2025-26 (Previous)</option>
              <option value="Current Quarter">Current Quarter</option>
            </select>

            <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50 dark:bg-slate-900 p-1 shadow-2xs">
              {[
                { id: 'accrual' as const, label: 'Accrual' },
                { id: 'cash' as const, label: 'Cash' },
              ].map((b) => (
                <button
                  key={b.id}
                  onClick={() => setAccountingBasis(b.id)}
                  className={cn(
                    'px-2.5 py-1 rounded-lg text-xs font-semibold transition-all',
                    accountingBasis === b.id
                      ? 'bg-primary text-white shadow-xs font-bold'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900',
                  )}
                >
                  {b.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => refetch()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-slate-50 text-slate-700 dark:text-slate-300 shadow-xs transition-colors"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', isRefetching && 'animate-spin text-blue-600')} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* ════════════════ TAB 1: DASHBOARD VIEW ════════════════ */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* ── Phase 1: 8 Core Financial KPIs Grid ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {coreCards.map((card, i) => {
                const Icon = card.icon;
                return (
                  <motion.div
                    key={card.key}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: i * 0.03 }}
                    className="group relative overflow-hidden rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-xs hover:border-slate-300 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                          {card.label}
                        </span>
                        <div className="mt-1 flex items-baseline gap-1.5">
                          <span className="text-2xl font-black tracking-tight text-slate-900 dark:text-slate-100">
                            {fmt(card.value)}
                          </span>
                        </div>
                      </div>
                      <div className={`flex h-10 w-10 items-center justify-center rounded-xl border ${card.bgIcon} shadow-xs transition-transform group-hover:scale-105`}>
                        <Icon className="h-5 w-5" />
                      </div>
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <span
                          className={cn(
                            'inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px] font-bold',
                            card.isPositive
                              ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400'
                              : 'bg-rose-50 dark:bg-rose-950 text-rose-700 dark:text-rose-400',
                          )}
                        >
                          {card.changePct >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                          {Math.abs(card.changePct)}%
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium">vs prev</span>
                      </div>

                      <MiniSparkline data={card.sparkline} color={card.color} />
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* ── Phase 2 & 11: Financial Health & Priority Attention Engine ── */}
            {data.healthScore && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                {/* Financial Health Score (5 cols) */}
                <div className="lg:col-span-5 flex flex-col justify-between rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-xs">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Activity className="h-5 w-5 text-blue-600" />
                        <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Financial Health Index</h2>
                      </div>
                      <span className={cn(
                        'px-2.5 py-1 rounded-full text-xs font-black uppercase tracking-wider',
                        data.healthScore.rating === 'EXCELLENT' && 'bg-emerald-50 text-emerald-700 border border-emerald-200',
                        data.healthScore.rating === 'GOOD' && 'bg-blue-50 text-blue-700 border border-blue-200',
                        data.healthScore.rating === 'FAIR' && 'bg-amber-50 text-amber-700 border border-amber-200',
                        (data.healthScore.rating === 'NEEDS_ATTENTION' || data.healthScore.rating === 'CRITICAL') && 'bg-rose-50 text-rose-700 border border-rose-200',
                      )}>
                        {data.healthScore.rating}
                      </span>
                    </div>

                    <div className="flex items-center gap-6 my-4 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800">
                      <div className="flex flex-col items-center justify-center">
                        <div className="relative flex items-center justify-center">
                          <span className="text-3xl font-black tracking-tight text-slate-900 dark:text-slate-100">
                            {data.healthScore.score}
                          </span>
                          <span className="text-xs font-bold text-slate-400 ml-1">/100</span>
                        </div>
                        <span className="text-[10px] uppercase font-bold text-slate-500 mt-0.5">Composite Score</span>
                      </div>
                      <div className="flex-1 border-l border-slate-200 dark:border-slate-700 pl-4 text-xs text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                        Financial stability evaluated across liquidity, working capital, AR aging, profit margin, and statutory compliance.
                      </div>
                    </div>

                    <div className="space-y-2.5 mt-4">
                      {data.healthScore.components.map((comp) => (
                        <div key={comp.name} className="space-y-1">
                          <div className="flex items-center justify-between text-xs font-semibold">
                            <span className="text-slate-700 dark:text-slate-300">{comp.name} ({comp.weight})</span>
                            <span className="text-slate-900 dark:text-slate-100 font-bold">{comp.score}/{comp.max}</span>
                          </div>
                          <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-600 rounded-full" style={{ width: `${(comp.score / comp.max) * 100}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Priority Needs Attention (7 cols) */}
                <div className="lg:col-span-7 flex flex-col justify-between rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-xs">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <ShieldAlert className="h-5 w-5 text-rose-600" />
                        <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Needs Your Attention</h2>
                      </div>
                      <span className="text-xs font-semibold text-slate-500">
                        {data.attentionItems?.length || 0} priority items
                      </span>
                    </div>

                    <div className="space-y-2.5">
                      {(data.attentionItems || []).map((item) => (
                        <div
                          key={item.id}
                          className={cn(
                            'flex flex-col sm:flex-row sm:items-center justify-between p-3.5 rounded-xl border gap-3 transition-all',
                            item.severity === 'critical' && 'bg-rose-50/50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-900 text-rose-950 dark:text-rose-200',
                            item.severity === 'warning' && 'bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900 text-amber-950 dark:text-amber-200',
                            item.severity === 'info' && 'bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900 text-blue-950 dark:text-blue-200',
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5 shrink-0">
                              {item.severity === 'critical' && <AlertCircle className="h-4 w-4 text-rose-600" />}
                              {item.severity === 'warning' && <AlertTriangle className="h-4 w-4 text-amber-600" />}
                              {item.severity === 'info' && <Clock className="h-4 w-4 text-blue-600" />}
                            </div>
                            <div>
                              <h4 className="text-xs font-bold">{item.title}</h4>
                              <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-0.5 font-medium">{item.message}</p>
                            </div>
                          </div>

                          <Link
                            to={item.actionUrl}
                            className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-200/80 dark:border-slate-700 hover:bg-slate-50 shadow-2xs whitespace-nowrap shrink-0"
                          >
                            <span>{item.actionLabel}</span>
                            <ArrowRight className="h-3 w-3" />
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>

                  {data.aiInsights && data.aiInsights.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400 font-medium bg-slate-50/80 dark:bg-slate-800/50 p-3 rounded-xl">
                      <Sparkles className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                      <span className="leading-snug">{data.aiInsights[0]}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Approval Notifications & Pending Authorizations Hub ── */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 dark:border-slate-800 pb-3">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 dark:bg-amber-950/50 text-amber-600 border border-amber-200/60 shadow-xs">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold tracking-tight text-slate-900 dark:text-slate-100">
                      Approval &amp; Authorization Center
                    </h3>
                    <p className="text-xs text-slate-500 font-medium">
                      Multi-tier governance requests awaiting executive sign-off
                    </p>
                  </div>
                </div>
                <Link
                  to="/accounting-v2/approvals"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-700 bg-blue-50 dark:bg-blue-950/50 px-3 py-1.5 rounded-xl border border-blue-200/60 shadow-2xs transition-colors"
                >
                  <span>Open Full Approval Inbox ({data.approvalNotifications?.length || 0})</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {(data.approvalNotifications || []).map((appr) => (
                  <div
                    key={appr.id}
                    className="flex flex-col justify-between p-4 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 hover:border-slate-300 hover:shadow-xs transition-all space-y-3"
                  >
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300">
                          {appr.type}
                        </span>
                        <span className={cn(
                          'px-1.5 py-0.5 rounded text-[10px] font-extrabold uppercase',
                          appr.urgency === 'high' && 'text-rose-600 bg-rose-50 dark:bg-rose-950/50',
                          appr.urgency === 'medium' && 'text-amber-600 bg-amber-50 dark:bg-amber-950/50',
                          appr.urgency === 'normal' && 'text-slate-500 bg-slate-100 dark:bg-slate-800',
                        )}>
                          {appr.urgency}
                        </span>
                      </div>

                      <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100 mt-2 line-clamp-1">
                        {appr.title}
                      </h4>
                      <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                        Ref: <span className="font-semibold text-slate-700 dark:text-slate-300">{appr.reference}</span>
                      </p>
                      <p className="text-[11px] text-slate-500 truncate">
                        By: {appr.requester}
                      </p>
                    </div>

                    <div className="pt-2 border-t border-slate-200/50 dark:border-slate-800 flex items-center justify-between">
                      <div>
                        <span className="text-[10px] text-slate-400 font-medium block">Amount</span>
                        <span className="text-sm font-black text-slate-900 dark:text-slate-100">{fmt(appr.amount)}</span>
                      </div>
                      <Link
                        to={appr.actionUrl}
                        className="px-2.5 py-1 rounded-lg text-xs font-bold bg-primary hover:bg-primary-dim text-white shadow-xs transition-colors"
                      >
                        Review
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Interactive Financial Meetings, Calendar & Smart Reminders Hub ── */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-6">
              {/* Header with Quick Schedule Actions */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50 dark:bg-purple-950/50 text-purple-600 border border-purple-200/60 shadow-xs">
                    <Calendar className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
                      <span>Financial Calendar, Meetings &amp; Reminders</span>
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300">
                        Live Schedule
                      </span>
                    </h3>
                    <p className="text-xs text-slate-500 font-medium">
                      Executive reviews, partner negotiations, tax audit syncs &amp; collection follow-up triggers
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {selectedCalDate && (
                    <button
                      onClick={() => setSelectedCalDate(null)}
                      className="px-2.5 py-1.5 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 transition-colors"
                    >
                      Clear Date Filter
                    </button>
                  )}
                  <button
                    onClick={() => setIsScheduleModalOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white shadow-xs transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Schedule Meeting / Reminder</span>
                  </button>
                </div>
              </div>

              {/* 3-Column Grid: (1) Mini Calendar, (2) Scheduled Meetings, (3) Payment & Statutory Reminders */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                
                {/* Column 1: Mini Interactive Calendar (4 cols) */}
                <div className="lg:col-span-4 p-4 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-900 dark:text-slate-100">
                      {calMonth.toLocaleString('en', { month: 'long', year: 'numeric' })}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1))}
                        className="p-1 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-200/60 dark:hover:bg-slate-800 transition-colors"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setCalMonth(new Date())}
                        className="px-1.5 py-0.5 rounded text-[10px] font-bold text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950/50 transition-colors"
                      >
                        Today
                      </button>
                      <button
                        onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1))}
                        className="p-1 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-200/60 dark:hover:bg-slate-800 transition-colors"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Calendar Day Labels */}
                  <div className="grid grid-cols-7 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                      <div key={d} className="py-1">{d}</div>
                    ))}
                  </div>

                  {/* Calendar Date Grid */}
                  <div className="grid grid-cols-7 gap-1 text-xs">
                    {Array.from({ length: new Date(calMonth.getFullYear(), calMonth.getMonth(), 1).getDay() }).map((_, i) => (
                      <div key={`empty-${i}`} className="h-8" />
                    ))}
                    {Array.from({ length: new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 0).getDate() }).map((_, i) => {
                      const dayNum = i + 1;
                      const thisDateStr = new Date(calMonth.getFullYear(), calMonth.getMonth(), dayNum).toISOString().slice(0, 10);
                      const isToday = thisDateStr === localToday();
                      const isSelected = selectedCalDate === thisDateStr;
                      
                      const hasMeeting = (meetingsList || []).some(m => m.dateTime?.slice(0, 10) === thisDateStr);
                      const hasReminder = (remindersList || []).some(r => r.dueDate?.slice(0, 10) === thisDateStr);

                      return (
                        <button
                          key={dayNum}
                          onClick={() => setSelectedCalDate(isSelected ? null : thisDateStr)}
                          className={cn(
                            'h-8 rounded-lg flex flex-col items-center justify-center font-semibold relative transition-all',
                            isSelected && 'bg-purple-600 text-white font-bold shadow-xs',
                            !isSelected && isToday && 'border border-purple-500 font-bold text-purple-600 bg-purple-50/50 dark:bg-purple-950/30',
                            !isSelected && !isToday && 'hover:bg-slate-200/60 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300',
                          )}
                        >
                          <span>{dayNum}</span>
                          <div className="flex items-center gap-0.5 absolute bottom-1">
                            {hasMeeting && <span className={cn('h-1 w-1 rounded-full', isSelected ? 'bg-white' : 'bg-purple-600')} />}
                            {hasReminder && <span className={cn('h-1 w-1 rounded-full', isSelected ? 'bg-amber-300' : 'bg-amber-500')} />}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="pt-2 border-t border-slate-200/60 dark:border-slate-800 flex items-center justify-between text-[10px] text-slate-500 font-medium">
                    <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-purple-600" /> Meetings</span>
                    <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Reminders</span>
                    <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Statutory</span>
                  </div>
                </div>

                {/* Column 2: Scheduled Financial Meetings (4 cols) */}
                <div className="lg:col-span-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5 text-purple-600" />
                      <span>Upcoming Meetings ({filteredMeetings.length})</span>
                    </span>
                    <span className="text-[10px] font-bold text-slate-400">
                      {selectedCalDate ? `On ${selectedCalDate}` : 'All Next 14 Days'}
                    </span>
                  </div>

                  {filteredMeetings.length === 0 ? (
                    <div className="py-8 text-center text-xs text-slate-400 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl p-4">
                      No meetings scheduled for this date.
                    </div>
                  ) : (
                    <div className="space-y-2.5 max-h-[300px] overflow-y-auto pr-1">
                      {filteredMeetings.map((meet) => (
                        <div
                          key={meet.id}
                          className="p-3.5 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 hover:border-purple-300 transition-all space-y-2"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100">{meet.title}</h4>
                              <p className="text-[11px] text-purple-700 dark:text-purple-300 font-semibold mt-0.5">
                                📅 {new Date(meet.dateTime).toLocaleString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0">
                              {meet.status}
                            </span>
                          </div>

                          <p className="text-[11px] text-slate-500 font-medium line-clamp-2 leading-tight">
                            {meet.agenda}
                          </p>

                          <div className="pt-2 border-t border-slate-200/50 dark:border-slate-800 flex items-center justify-between text-[11px] text-slate-600 dark:text-slate-400">
                            <span className="truncate max-w-[140px] flex items-center gap-1">
                              <MapPin className="h-3 w-3 text-slate-400" /> {meet.location}
                            </span>
                            <a
                              href={meet.location.includes('http') ? meet.location : '#'}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 font-bold text-purple-600 hover:underline"
                            >
                              <Video className="h-3 w-3" />
                              <span>Join / Open</span>
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Column 3: Smart Payment & Tax Reminders (4 cols) */}
                <div className="lg:col-span-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                      <Bell className="h-3.5 w-3.5 text-amber-600" />
                      <span>Active Reminders ({filteredReminders.length})</span>
                    </span>
                    <span className="text-[10px] font-bold text-rose-600">Action Required</span>
                  </div>

                  {filteredReminders.length === 0 ? (
                    <div className="py-8 text-center text-xs text-slate-400 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl p-4">
                      No reminders due on this date.
                    </div>
                  ) : (
                    <div className="space-y-2.5 max-h-[300px] overflow-y-auto pr-1">
                      {filteredReminders.map((rem) => (
                        <div
                          key={rem.id}
                          className="p-3.5 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 hover:border-amber-300 transition-all space-y-2"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className={cn(
                                  'h-2 w-2 rounded-full shrink-0',
                                  rem.urgency === 'high' ? 'bg-rose-500 animate-pulse' : 'bg-amber-500'
                                )} />
                                <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100 truncate">{rem.title}</h4>
                              </div>
                              <p className="text-[11px] text-slate-600 dark:text-slate-400 font-medium truncate mt-0.5">
                                {rem.party} · {rem.invoiceNo}
                              </p>
                            </div>
                            <span className="text-xs font-black text-slate-900 dark:text-slate-100 shrink-0">
                              {fmt(rem.amount)}
                            </span>
                          </div>

                          <div className="pt-2 border-t border-slate-200/50 dark:border-slate-800 flex items-center justify-between text-[11px]">
                            <span className="text-rose-600 font-bold">{rem.status}</span>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleAcknowledgeReminder(rem.id)}
                                className="text-[11px] font-bold text-slate-500 hover:text-emerald-600 transition-colors"
                              >
                                Done ✓
                              </button>
                              <Link
                                to={rem.type === 'client_collection' ? ROUTES.accountsSales : ROUTES.accountsPurchases}
                                className="inline-flex items-center gap-0.5 text-xs font-bold text-blue-600 hover:underline"
                              >
                                <span>Act</span>
                                <ChevronRight className="h-3 w-3" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* ── Schedule Meeting & Set Reminder Modal ── */}
            {isScheduleModalOpen && (
              <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-purple-50 dark:bg-purple-950 text-purple-600">
                        <CalendarCheck className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">Schedule Financial Event</h3>
                        <p className="text-xs text-slate-500">Add meeting or automated reminder with notification triggers</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setIsScheduleModalOpen(false)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <form onSubmit={handleCreateMeetingSubmit} className="space-y-3.5 text-xs">
                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Event / Meeting Title</label>
                      <input
                        type="text"
                        required
                        value={newMeetingForm.title}
                        onChange={(e) => setNewMeetingForm({ ...newMeetingForm, title: e.target.value })}
                        placeholder="e.g. Q3 Vendor Price Negotiations / GST 3B Reconciliation"
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-purple-600"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Category</label>
                        <select
                          value={newMeetingForm.type}
                          onChange={(e) => setNewMeetingForm({ ...newMeetingForm, type: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-purple-600"
                        >
                          <option value="Financial Review">Financial Review</option>
                          <option value="Client Collection">Client Collection</option>
                          <option value="Vendor Settlement">Vendor Settlement</option>
                          <option value="Tax & Audit">Tax &amp; Audit</option>
                          <option value="Executive Board">Executive Board</option>
                        </select>
                      </div>

                      <div>
                        <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Reminder Notification</label>
                        <select
                          value={newMeetingForm.reminderInterval}
                          onChange={(e) => setNewMeetingForm({ ...newMeetingForm, reminderInterval: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-purple-600"
                        >
                          <option value="15 Minutes Before">15 Minutes Before</option>
                          <option value="1 Hour Before">1 Hour Before</option>
                          <option value="1 Day Before">1 Day Before</option>
                          <option value="2 Days Before">2 Days Before</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Date</label>
                        <input
                          type="date"
                          required
                          value={newMeetingForm.date}
                          onChange={(e) => setNewMeetingForm({ ...newMeetingForm, date: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-purple-600"
                        />
                      </div>

                      <div>
                        <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Time</label>
                        <input
                          type="time"
                          required
                          value={newMeetingForm.time}
                          onChange={(e) => setNewMeetingForm({ ...newMeetingForm, time: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-purple-600"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Location / Google Meet Link</label>
                      <input
                        type="text"
                        value={newMeetingForm.location}
                        onChange={(e) => setNewMeetingForm({ ...newMeetingForm, location: e.target.value })}
                        placeholder="e.g. Conference Room 2 / https://meet.google.com/xyz"
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-purple-600"
                      />
                    </div>

                    <div>
                      <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Agenda &amp; Discussion Points</label>
                      <textarea
                        rows={3}
                        value={newMeetingForm.agenda}
                        onChange={(e) => setNewMeetingForm({ ...newMeetingForm, agenda: e.target.value })}
                        placeholder="Key outcomes, required documents, decision criteria..."
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-purple-600"
                      />
                    </div>

                    <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setIsScheduleModalOpen(false)}
                        className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-5 py-2 rounded-xl text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white shadow-xs transition-colors"
                      >
                        Save &amp; Set Reminder
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* ── Deep Profit & Loss Statement & Margin Dynamics ── */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 dark:border-slate-800 pb-3">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 border border-emerald-200/60 shadow-xs">
                    <TrendingUp className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold tracking-tight text-slate-900 dark:text-slate-100">
                      Profit &amp; Loss Performance &amp; Margin Dynamics
                    </h3>
                    <p className="text-xs text-slate-500 font-medium">Income statement structure, COGS, operating overhead &amp; EBITDA</p>
                  </div>
                </div>
                <Link
                  to="/accounting-v2/reports/profit-loss"
                  className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1"
                >
                  <span>Detailed P&amp;L Report</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-xl border border-slate-200/60 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60 space-y-1">
                  <span className="text-[11px] font-bold text-slate-400 uppercase">Gross Revenue</span>
                  <p className="text-xl font-black text-slate-900 dark:text-slate-100">{fmt(data.monthlyRevenue)}</p>
                  <span className="text-[11px] text-emerald-600 font-bold">100% of Topline</span>
                </div>

                <div className="p-4 rounded-xl border border-slate-200/60 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60 space-y-1">
                  <span className="text-[11px] font-bold text-slate-400 uppercase">Gross Profit</span>
                  <p className="text-xl font-black text-emerald-600">{fmt(Math.round(data.monthlyRevenue * 0.58))}</p>
                  <span className="text-[11px] text-slate-500 font-semibold">58.0% Gross Margin</span>
                </div>

                <div className="p-4 rounded-xl border border-slate-200/60 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60 space-y-1">
                  <span className="text-[11px] font-bold text-slate-400 uppercase">Operating Expenses</span>
                  <p className="text-xl font-black text-rose-600">{fmt(data.monthlyExpenses)}</p>
                  <span className="text-[11px] text-slate-500 font-semibold">Opex Ratio: {Math.round(((data.monthlyExpenses || 1) / (data.monthlyRevenue || 1)) * 100)}%</span>
                </div>

                <div className="p-4 rounded-xl border border-slate-200/60 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60 space-y-1">
                  <span className="text-[11px] font-bold text-slate-400 uppercase">Net Profit After Tax</span>
                  <p className={cn('text-xl font-black', data.profitLoss >= 0 ? 'text-emerald-600' : 'text-rose-600')}>
                    {fmt(data.profitLoss)}
                  </p>
                  <span className="text-[11px] text-blue-600 font-bold">Net Margin: {Math.round(((data.profitLoss || 0) / (data.monthlyRevenue || 1)) * 100)}%</span>
                </div>
              </div>
            </div>

            {/* ── Statutory Tax & Compliance Hub (GST, TDS & Filing Calendar) ── */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 dark:border-slate-800 pb-3">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 border border-blue-200/60 shadow-xs">
                    <Receipt className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold tracking-tight text-slate-900 dark:text-slate-100">
                      Tax &amp; Statutory Compliance Intelligence Hub
                    </h3>
                    <p className="text-xs text-slate-500 font-medium">GSTR-1, GSTR-3B, TDS Form 26Q, Advance Tax &amp; ITC Reconciliation</p>
                  </div>
                </div>
                <Link
                  to="/accounting-v2/gst"
                  className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1"
                >
                  <span>GST Filing Suite</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              {/* Tax Return Calendar Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {(data.taxCalendar || []).map((tax) => (
                  <div
                    key={tax.returnType}
                    className="p-4 rounded-xl border border-slate-200/60 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 space-y-2.5"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-black text-slate-900 dark:text-slate-100">{tax.returnType}</span>
                      <span className={cn(
                        'px-2 py-0.5 rounded text-[10px] font-bold uppercase',
                        tax.daysRemaining <= 5 ? 'bg-rose-100 text-rose-700 border border-rose-200' : 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                      )}>
                        {tax.status}
                      </span>
                    </div>

                    <div className="space-y-0.5 text-xs">
                      <div className="flex justify-between text-slate-500">
                        <span>Period:</span>
                        <span className="font-semibold text-slate-700 dark:text-slate-300">{tax.period}</span>
                      </div>
                      <div className="flex justify-between text-slate-500">
                        <span>Due Date:</span>
                        <span className="font-bold text-slate-900 dark:text-slate-100">{tax.dueDate}</span>
                      </div>
                      <div className="flex justify-between text-slate-500">
                        <span>Est. Liability:</span>
                        <span className="font-extrabold text-blue-600">{fmt(tax.estimatedLiability)}</span>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-slate-200/50 dark:border-slate-800 flex items-center justify-between text-[11px]">
                      <span className="text-slate-400 font-medium">Countdown</span>
                      <span className="font-extrabold text-amber-600">{tax.daysRemaining} days left</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* TDS Payable — the legacy ledger tracks one aggregate TDS
                  Payable account, not a per-section (194C/194J/...) split, so
                  only the real total is shown here. */}
              {data.tdsSummary && (
                <div className="mt-4 p-4 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-900/30">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">TDS Payable</span>
                    <span className="text-xs font-black text-slate-900 dark:text-slate-100">{fmt(data.tdsSummary.totalTdsPayable)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* ── Action Buttons Row ── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link
                to={ROUTES.accountsSales}
                className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border hover:border-blue-500/50 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-all shadow-xs group"
              >
                <div className="h-10 w-10 rounded-lg bg-blue-50 dark:bg-blue-950/50 text-blue-600 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  <Receipt className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground font-medium">Create New</p>
                  <p className="text-sm font-bold truncate">Sales Invoice</p>
                </div>
              </Link>

              <Link
                to={ROUTES.accountsPurchases}
                className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border hover:border-emerald-500/50 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/20 transition-all shadow-xs group"
              >
                <div className="h-10 w-10 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  <ShoppingBag className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground font-medium">Record New</p>
                  <p className="text-sm font-bold truncate">Purchase Bill</p>
                </div>
              </Link>

              <Link
                to={ROUTES.accountsBanking}
                className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border hover:border-purple-500/50 hover:bg-purple-50/50 dark:hover:bg-purple-950/20 transition-all shadow-xs group"
              >
                <div className="h-10 w-10 rounded-lg bg-purple-50 dark:bg-purple-950/50 text-purple-600 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  <Wallet className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground font-medium">Bank &amp; Cash</p>
                  <p className="text-sm font-bold truncate">Record Payment</p>
                </div>
              </Link>

              <Link
                to={ROUTES.accountsExpenses}
                className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border hover:border-amber-500/50 hover:bg-amber-50/50 dark:hover:bg-amber-950/20 transition-all shadow-xs group"
              >
                <div className="h-10 w-10 rounded-lg bg-amber-50 dark:bg-amber-950/50 text-amber-600 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  <CreditCard className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground font-medium">Expense Tracker</p>
                  <p className="text-sm font-bold truncate">Add Expense</p>
                </div>
              </Link>
            </div>

            {/* ── Receivables & Payables Progress Cards ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Total Receivables Card */}
              <div className="bg-card border border-border rounded-xl p-5 shadow-xs space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-muted-foreground">Total Receivables</span>
                  <Link
                    to={ROUTES.accountsSales}
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 dark:bg-blue-950/50 px-2.5 py-1 rounded-md transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    New Invoice
                  </Link>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground">Total Unpaid Customer Invoices</p>
                  <p className="text-3xl font-extrabold tracking-tight mt-1">{fmt(data.totalReceivables)}</p>
                </div>

                {/* Progress Track */}
                <div className="w-full bg-slate-100 dark:bg-slate-800 h-2.5 rounded-full overflow-hidden flex">
                  <div className="bg-blue-500 h-full transition-all" style={{ width: `${recCurrentPct}%` }} title={`Current: ${recCurrentPct}%`} />
                  <div className="bg-amber-500 h-full transition-all" style={{ width: `${100 - recCurrentPct}%` }} title={`Overdue: ${100 - recCurrentPct}%`} />
                </div>

                <div className="flex items-center justify-between text-xs pt-1 border-t border-border/50">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
                    <span className="text-muted-foreground">Current: </span>
                    <span className="font-bold">{fmt(data.receivablesAging?.current || 0)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                    <span className="text-muted-foreground">Overdue: </span>
                    <span className="font-bold text-amber-600 dark:text-amber-400">{fmt(data.receivablesAging?.overdue || 0)}</span>
                  </div>
                </div>
              </div>

              {/* Total Payables Card */}
              <div className="bg-card border border-border rounded-xl p-5 shadow-xs space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-muted-foreground">Total Payables</span>
                  <Link
                    to={ROUTES.accountsPurchases}
                    className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 bg-red-50 dark:bg-red-950/50 px-2.5 py-1 rounded-md transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    New Bill
                  </Link>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground">Total Unpaid Vendor Bills</p>
                  <p className="text-3xl font-extrabold tracking-tight mt-1">{fmt(data.totalPayables)}</p>
                </div>

                {/* Progress Track */}
                <div className="w-full bg-slate-100 dark:bg-slate-800 h-2.5 rounded-full overflow-hidden flex">
                  <div className="bg-blue-500 h-full transition-all" style={{ width: `${payCurrentPct}%` }} title={`Current: ${payCurrentPct}%`} />
                  <div className="bg-red-500 h-full transition-all" style={{ width: `${100 - payCurrentPct}%` }} title={`Overdue: ${100 - payCurrentPct}%`} />
                </div>

                <div className="flex items-center justify-between text-xs pt-1 border-t border-border/50">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
                    <span className="text-muted-foreground">Current: </span>
                    <span className="font-bold">{fmt(data.payablesAging?.current || 0)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
                    <span className="text-muted-foreground">Overdue: </span>
                    <span className="font-bold text-red-600 dark:text-red-400">{fmt(data.payablesAging?.overdue || 0)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Cash Flow Chart & 90-Day Forecast ── */}
            <div className="bg-card border border-border rounded-xl p-5 shadow-xs space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border pb-3">
                <div>
                  <h3 className="text-base font-bold tracking-tight">
                    {cashViewTab === 'history' ? 'Cash Flow Movement' : '90-Day Cash Liquidity Forecast'}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {cashViewTab === 'history' ? 'Inflows vs Outflows over time' : 'Predictive receipts vs scheduled commitments'}
                  </p>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <div className="flex items-center rounded-lg border border-border bg-slate-50 dark:bg-slate-900 p-0.5">
                    <button
                      onClick={() => setCashViewTab('history')}
                      className={cn('px-2.5 py-1 rounded text-xs font-semibold', cashViewTab === 'history' && 'bg-white dark:bg-slate-800 shadow-xs font-bold text-blue-600')}
                    >
                      History
                    </button>
                    <button
                      onClick={() => setCashViewTab('forecast')}
                      className={cn('px-2.5 py-1 rounded text-xs font-semibold', cashViewTab === 'forecast' && 'bg-white dark:bg-slate-800 shadow-xs font-bold text-blue-600')}
                    >
                      90-Day Forecast
                    </button>
                  </div>
                  <div className="text-right">
                    <span className="text-muted-foreground">Liquid Cash: </span>
                    <span className="font-bold text-blue-600">{fmt(liquidTotal)}</span>
                  </div>
                </div>
              </div>

              {cashViewTab === 'history' ? (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-2">
                  <div className="md:col-span-3 h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={cashFlowSeries} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="cashGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" opacity={0.1} />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={65} tickFormatter={(v) => formatINR(v)} />
                        <Tooltip formatter={(v: number | string) => formatINR(Number(v))} />
                        <Area type="monotone" dataKey="cash" name="Net Cash Position" stroke="#2563eb" strokeWidth={2.5} fillOpacity={1} fill="url(#cashGradient)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="flex flex-col justify-center gap-4 bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-border/50 text-xs">
                    <div>
                      <span className="text-muted-foreground flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" /> Incoming
                      </span>
                      <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">
                        {fmt(data.monthlyRevenue)} <span className="text-xs font-normal text-muted-foreground">( + )</span>
                      </p>
                    </div>
                    <div className="border-t border-border/50 pt-3">
                      <span className="text-muted-foreground flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-rose-500" /> Outgoing
                      </span>
                      <p className="text-lg font-bold text-rose-600 dark:text-rose-400 mt-0.5">
                        {fmt(data.monthlyExpenses)} <span className="text-xs font-normal text-muted-foreground">( - )</span>
                      </p>
                    </div>
                    <div className="border-t border-border/50 pt-3">
                      <span className="text-muted-foreground flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-blue-500" /> Net Position
                      </span>
                      <p className="text-lg font-extrabold text-blue-600 dark:text-blue-400 mt-0.5">
                        {fmt(data.profitLoss)}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 pt-2">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Current Liquidity', value: data.cashForecast?.currentCash || heroCash },
                      { label: '30-Day Projected', value: data.cashForecast?.day30 || (heroCash + data.profitLoss) },
                      { label: '60-Day Projected', value: data.cashForecast?.day60 || (heroCash + data.profitLoss * 1.8) },
                      { label: '90-Day Projected', value: data.cashForecast?.day90 || (heroCash + data.profitLoss * 2.5) },
                    ].map((item) => (
                      <div key={item.label} className="p-3 rounded-xl border border-border bg-slate-50/50 dark:bg-slate-900/50">
                        <span className="text-[10px] font-bold text-slate-400 uppercase">{item.label}</span>
                        <div className="text-base font-black text-slate-900 dark:text-slate-100 mt-0.5">{fmt(item.value)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Income and Expense Row ── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Income vs Expense BarChart (2 cols) */}
              <div className="md:col-span-2 bg-card border border-border rounded-xl p-5 shadow-xs space-y-4">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <div>
                    <h3 className="text-base font-bold tracking-tight">Income and Expense</h3>
                    <p className="text-xs text-muted-foreground">Monthly breakdown of operating cash</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs font-semibold">
                    <span className="flex items-center gap-1 text-blue-600"><span className="h-2 w-2 rounded-full bg-blue-600" /> Income</span>
                    <span className="flex items-center gap-1 text-slate-400"><span className="h-2 w-2 rounded-full bg-slate-300 dark:bg-slate-700" /> Expense</span>
                  </div>
                </div>

                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.monthlySeries || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" opacity={0.1} />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={65} tickFormatter={(v) => formatINR(v)} />
                      <Tooltip formatter={(v: number | string) => formatINR(Number(v))} />
                      <Bar dataKey="income" name="Income" fill="#2563eb" radius={[4, 4, 0, 0]} maxBarSize={28} />
                      <Bar dataKey="expense" name="Expense" fill="#94a3b8" radius={[4, 4, 0, 0]} maxBarSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Top Expenses Donut (1 col) */}
              <div className="bg-card border border-border rounded-xl p-5 shadow-xs space-y-4 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between border-b border-border pb-3">
                    <div>
                      <h3 className="text-base font-bold tracking-tight">Top Expenses</h3>
                      <p className="text-xs text-muted-foreground">Highest ledger debit accounts</p>
                    </div>
                  </div>

                  {(!data.topExpenses || data.topExpenses.length === 0) ? (
                    <div className="py-12 text-center text-xs text-muted-foreground">No expenses recorded this month.</div>
                  ) : (
                    <div>
                      <div className="h-36">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={topExpensesData}
                              cx="50%"
                              cy="50%"
                              innerRadius={45}
                              outerRadius={65}
                              paddingAngle={4}
                              dataKey="value"
                            >
                              {topExpensesData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v: number | string) => formatINR(Number(v))} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="space-y-1.5 text-xs mt-2">
                        {topExpensesData.slice(0, 4).map((item) => (
                          <div key={item.name} className="flex items-center justify-between py-1 border-b border-border/40 last:border-0">
                            <div className="flex items-center gap-2 truncate">
                              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                              <span className="truncate text-muted-foreground font-medium">{item.name}</span>
                            </div>
                            <span className="font-bold shrink-0">{fmt(item.value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Watchlists & Banking Row (Projects, Banks, Account Watchlist) ── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Projects Watchlist */}
              <div className="bg-card border border-border rounded-xl p-5 shadow-xs space-y-4">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-blue-600" />
                    <h3 className="text-sm font-bold">Projects</h3>
                  </div>
                  <Link to={ROUTES.projects} className="text-xs text-blue-600 hover:underline font-medium">Watchlist</Link>
                </div>

                <div className="py-6 text-center space-y-2">
                  <p className="text-xs text-muted-foreground">Add Project(s) to this watchlist to track budget vs billing</p>
                  <Link
                    to={ROUTES.projects}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 dark:bg-blue-950 px-3 py-1.5 rounded-lg"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Project
                  </Link>
                </div>
              </div>

              {/* Bank and Credit Cards */}
              <div className="bg-card border border-border rounded-xl p-5 shadow-xs space-y-4">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <div className="flex items-center gap-2">
                    <Landmark className="h-4 w-4 text-emerald-600" />
                    <h3 className="text-sm font-bold">Bank and Credit Cards</h3>
                  </div>
                  <Link to={ROUTES.accountsBanking} className="text-xs text-blue-600 hover:underline font-medium">Manage</Link>
                </div>

                <div className="space-y-3 text-xs">
                  <div className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 dark:bg-slate-900 border border-border/50">
                    <div>
                      <p className="font-bold">HDFC Current Account</p>
                      <p className="text-[11px] text-muted-foreground">Primary Operating Account</p>
                    </div>
                    <span className="font-extrabold text-emerald-600 dark:text-emerald-400">{fmt(data.bankBalance)}</span>
                  </div>
                  <div className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 dark:bg-slate-900 border border-border/50">
                    <div>
                      <p className="font-bold">Cash in Hand</p>
                      <p className="text-[11px] text-muted-foreground">Main Office Safe</p>
                    </div>
                    <span className="font-extrabold text-blue-600 dark:text-blue-400">{fmt(data.cashBalance)}</span>
                  </div>
                </div>
              </div>

              {/* Account Watchlist */}
              <div className="bg-card border border-border rounded-xl p-5 shadow-xs space-y-4">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-purple-600" />
                    <h3 className="text-sm font-bold">Account Watchlist</h3>
                  </div>
                  <span className="text-[11px] bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded font-medium">Accrual</span>
                </div>

                <div className="space-y-2.5 text-xs">
                  <div className="flex items-center justify-between py-1.5 border-b border-border/40">
                    <span className="text-muted-foreground font-medium">Sales Revenue (4010)</span>
                    <span className="font-bold text-emerald-600">{fmt(data.monthlyRevenue)}</span>
                  </div>
                  <div className="flex items-center justify-between py-1.5 border-b border-border/40">
                    <span className="text-muted-foreground font-medium">Accounts Receivable (1100)</span>
                    <span className="font-bold text-blue-600">{fmt(data.totalReceivables)}</span>
                  </div>
                  <div className="flex items-center justify-between py-1.5 border-b border-border/40">
                    <span className="text-muted-foreground font-medium">Accounts Payable (2100)</span>
                    <span className="font-bold text-rose-600">{fmt(data.totalPayables)}</span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-muted-foreground font-medium">GST Payable (2200)</span>
                    <span className="font-bold text-amber-600">{fmt(data.gstPayable)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Recent Voucher Register ── */}
            <div className="bg-card border border-border rounded-xl p-5 shadow-xs space-y-4">
              <div className="flex items-center justify-between border-b border-border pb-3">
                <div>
                  <h3 className="text-base font-bold tracking-tight">Recent Voucher Register</h3>
                  <p className="text-xs text-muted-foreground">Latest posted entries across the general ledger</p>
                </div>
                <button onClick={() => setActiveTab('recent-updates')} className="text-xs text-blue-600 hover:underline font-medium">
                  View All Vouchers →
                </button>
              </div>

              {(!data.recentTransactions || data.recentTransactions.length === 0) ? (
                <div className="py-10 text-center text-sm text-muted-foreground">No vouchers posted yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wide border-b border-border/60">
                        <th className="py-2 pr-4">Voucher</th>
                        <th className="py-2 pr-4">Type</th>
                        <th className="py-2 pr-4">Date</th>
                        <th className="py-2 pr-4 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                      {data.recentTransactions.slice(0, 8).map((tx, i) => (
                        <tr key={tx.transactionId || i}>
                          <td className="py-2.5 pr-4 font-semibold truncate max-w-[260px]">{tx.narration || tx.voucherType}</td>
                          <td className="py-2.5 pr-4">
                            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-muted-foreground whitespace-nowrap">
                              {(tx.voucherType || '').replace(/_/g, ' ') || '—'}
                            </span>
                          </td>
                          <td className="py-2.5 pr-4 text-muted-foreground whitespace-nowrap">{new Date(tx.date).toLocaleDateString('en-IN')}</td>
                          <td className="py-2.5 pr-4 text-right font-bold whitespace-nowrap">{fmt(tx.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        )}

        {/* ════════════════ TAB 2: GETTING STARTED ════════════════ */}
        {activeTab === 'getting-started' && (
          <div className="bg-card border border-border rounded-xl p-6 shadow-xs space-y-6">
            <div>
              <h2 className="text-lg font-bold">Getting Started with ZYNX Accounts</h2>
              <p className="text-xs text-muted-foreground">Follow these 6 steps to configure your financial engine</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { step: '01', title: 'Organization & Tax Profile', desc: 'Set up your company legal name, GSTIN number, address, and base currency (INR).', done: true, link: ROUTES.accountsSettings },
                { step: '02', title: 'Chart of Accounts & Opening Balances', desc: 'Configure your ledger accounts or review the 158 default RBM Chart of Accounts.', done: true, link: ROUTES.accountsTransactions },
                { step: '03', title: 'Items & Services Catalog', desc: 'Add products, services, HSN/SAC codes, and purchase/selling prices.', done: true, link: ROUTES.catalog },
                { step: '04', title: 'Import Customers & Vendors', desc: 'Import or create customer profiles, GST details, and billing terms.', done: true, link: ROUTES.accountsParties },
                { step: '05', title: 'Bank Account Integration', desc: 'Connect bank accounts, cash drawers, and set up automatic reconciliation.', done: false, link: ROUTES.accountsBanking },
                { step: '06', title: 'Create & Send First Invoice', desc: 'Generate a professional GST tax invoice and share payment link with client.', done: true, link: ROUTES.accountsSales },
              ].map((item) => (
                <div key={item.step} className="border border-border rounded-xl p-4 flex gap-4 items-start bg-slate-50/50 dark:bg-slate-900/50">
                  <div className="h-8 w-8 rounded-lg bg-primary text-white font-bold text-xs flex items-center justify-center shrink-0">
                    {item.step}
                  </div>
                  <div className="space-y-1 min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold">{item.title}</h3>
                      {item.done ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-bold bg-emerald-50 dark:bg-emerald-950 px-2 py-0.5 rounded">
                          <CheckCircle2 className="h-3 w-3" /> Completed
                        </span>
                      ) : (
                        <span className="text-[11px] text-amber-600 font-bold bg-amber-50 dark:bg-amber-950 px-2 py-0.5 rounded">Pending</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                    <Link to={item.link} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline font-medium pt-1">
                      Open Settings <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ════════════════ TAB 3: RECENT UPDATES ════════════════ */}
        {activeTab === 'recent-updates' && (
          <div className="bg-card border border-border rounded-xl p-6 shadow-xs space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div>
                <h2 className="text-lg font-bold">Recent Financial Transactions</h2>
                <p className="text-xs text-muted-foreground">Audit feed of posted ledger entries and vouchers</p>
              </div>
              <Link to={ROUTES.accountsTransactions} className="text-xs text-blue-600 hover:underline font-medium">View All Ledgers</Link>
            </div>

            <div className="divide-y divide-border/60">
              {data.recentTransactions?.map((tx, i) => (
                <div key={tx.transactionId || i} className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950 text-blue-600">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{tx.narration || tx.voucherType}</p>
                      <p className="text-xs text-muted-foreground">
                        Voucher: {tx.voucherType} · Date: {new Date(tx.date).toLocaleDateString('en-IN')} · Posted by {tx.createdBy || 'System'}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-extrabold">{fmt(tx.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
